import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from "@reduxjs/toolkit";
import {IPublishInfo} from "common";
import {HealthReport} from "../../interfaces/HealthReport";
import {IConditionItem, Trigger} from "../../interfaces/Trigger";
import { ProjectStates } from '../../interfaces/ProjectStates';
import { ProjectRevision } from '../../interfaces/ProjectRevision';

function validateStepTrigger(state, stepId: string, conditionIndex: number, triggerIndex: number)
{
	if(state.stepConditions[stepId] &&
		state.stepConditions[stepId][conditionIndex].triggers &&
		state.stepConditions[stepId][conditionIndex].triggers.length > triggerIndex)
	{
		return true;
	}
	return false;
}
interface SequenceEditorOptions {
	showSelectedConnections: boolean;
	showChecklistConnections: boolean;
}

export interface IProjectPermissions {
	canView: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	canReview: boolean;
	canManage: boolean;
}

const defaultSequenceEditorOptions: SequenceEditorOptions = {
	showSelectedConnections: false,
	showChecklistConnections: true,
};

export interface IScenarioState {
	elementData: Record<string, any>; // Assuming elementData is a dictionary with string keys
	scenarioId: string;
	scenarioGQLId: string;
	initialSequenceId: string;
	currentStepId: string;
	scenarioPublishInfo: IPublishInfo | null;
	update: number;
	clipboard: any | null;
	renameTarget: string;
	selectedSteps: string[]; // Array of step IDs
	stepHistorySimulation: string[];
	sequenceEditorOptions: SequenceEditorOptions;
	sequencesTopbarDraw: string[];
	stepConditions: Record<string, IConditionItem[]>;
	stepConditionsClipboard: IConditionItem | Trigger | null;
	healthReports: Record<string, HealthReport[]>;
	projectState: ProjectStates;
	projectSize: number;
	permissions: IProjectPermissions;
	projectSaveFileId: string;
	currentRevision: ProjectRevision;
	revisions: ProjectRevision[];
	highestRevisionNumber: number;
}

const initialState: IScenarioState = {
	elementData: {},
	scenarioId: "",
	scenarioGQLId: "",
	initialSequenceId: "",
	currentStepId: "",
	scenarioPublishInfo: null,
	update: 0,
	clipboard: null,
	renameTarget: "",
	selectedSteps: new Array<string>(),
	stepHistorySimulation: new Array<string>(),
	sequenceEditorOptions: { ...defaultSequenceEditorOptions },
	sequencesTopbarDraw: [],
	stepConditions: {},
	stepConditionsClipboard: null,
	healthReports: {},
	projectState: ProjectStates.InProgress,
	projectSize: 0,
	permissions: {
		canView: false,
		canUpdate: false,
		canDelete: false,
		canReview: false,
		canManage: false,
	},
	projectSaveFileId: '',
	currentRevision: {
		projectId: '',
		revisionName: '-',
		revisionNotes: '',
		revisionNumber: -1,
	},
	revisions: [],
	highestRevisionNumber: -1,
};

const ScenarioSlice = createSlice({
	name: "scenario",
	initialState,
	reducers: {
		loadStepConditions(state, action)
		{
			if(action.payload)
				state.stepConditions = action.payload;
		},
		copyStepConditionOrAction(state, action: PayloadAction<IConditionItem | Trigger>)
		{
			state.stepConditionsClipboard = action.payload;
		},
		pasteStepConditions(state, action)
		{
			if(!state.stepConditions[action.payload.stepId])
			{
				state.stepConditions[action.payload.stepId] = [];
			}

			state.stepConditions[action.payload.stepId] = action.payload.clipboard;
		},
		addStepCondition(state, action)
		{
			if(!state.stepConditions[action.payload.stepId])
			{
				state.stepConditions[action.payload.stepId] = [];
			}

			state.stepConditions[action.payload.stepId].push(action.payload.condition);

		},
		removeStepCondition(state, action)
		{
			if(state.stepConditions[action.payload.stepId] && state.stepConditions[action.payload.stepId].length > action.payload.index)
			{
				const tempArray = state.stepConditions[action.payload.stepId];
				tempArray.splice(action.payload.index, 1);
				state.stepConditions[action.payload.stepId] = tempArray;
			}
		},
		setMonitoredVariable(state, action)
		{
			if(state.stepConditions[action.payload.stepId] && state.stepConditions[action.payload.stepId][action.payload.index])
			{
				state.stepConditions[action.payload.stepId][action.payload.index].monitoredVariable = action.payload.monitoredVariable;
			}
		},
		setConditionExpression(state, action)
		{
			if(state.stepConditions[action.payload.stepId] && state.stepConditions[action.payload.stepId][action.payload.index])
			{
				state.stepConditions[action.payload.stepId][action.payload.index].conditionExpression = action.payload.variable;
			}
		},
		addStepTrigger(state, action)
		{
			if(state.stepConditions[action.payload.stepId] && state.stepConditions[action.payload.stepId][action.payload.index])
			{
				if(!state.stepConditions[action.payload.stepId][action.payload.index].triggers)
				{
					state.stepConditions[action.payload.stepId][action.payload.index].triggers = [];
				}
				state.stepConditions[action.payload.stepId][action.payload.index].triggers.push(action.payload.trigger);
			}
		},
		removeStepTrigger(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				const tempArray = state.stepConditions[action.payload.stepId][action.payload.index].triggers;
				tempArray.splice(action.payload.triggerIndex, 1);
				state.stepConditions[action.payload.stepId][action.payload.index].triggers = tempArray;
			}
		},
		setStepTriggerType(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].type = action.payload.value;
			}
		},
		setStepTriggerTargetStep(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].targetStep = action.payload.value;
			}
		},
		setStepTriggerUrl(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].url = action.payload.value;
			}
		},
		setStepTriggerProjectLinkMode(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].projectLinkMode = action.payload.value;
			}
		},
		setStepTriggerOnlineScenario(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].onlineScenarioName = action.payload.name;
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].scenarioId = action.payload.id;
			}
		},
		setStepTriggerOfflineScenarioName(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].offlineScenarioName = action.payload.value;
			}
		},
		setStepTriggerTargetObject(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].targetObject = action.payload.value;
			}
		},
		setStepTriggerObjectVisibilityMode(state, action)
		{
			if(validateStepTrigger(state, action.payload.stepId, action.payload.index, action.payload.triggerIndex))
			{
				state.stepConditions[action.payload.stepId][action.payload.index].triggers[action.payload.triggerIndex].targetObjectVisibility = action.payload.value;
			}
		},
		setElementData(state, action)
		{
			const { dataId, data } = action.payload;

			state.elementData[dataId] = { ...data };

			state.update = state.update + 1;
		},
		setElementProperty(state, action)
		{
			const { elementId, propertyName, propertyValue } = action.payload;

			if (!state.elementData[elementId])
			{
				state.elementData[elementId] = {};
			}

			state.elementData[elementId][propertyName] = propertyValue;

			state.update = state.update + 1;
		},
		removeElementData(state, action)
		{
			delete state.elementData[action.payload];

			state.update = state.update + 1;
		},
		setScenario(state, action)
		{
			state.scenarioId = action.payload; // payload = scenario id

			state.update = state.update + 1;
		},
		setInitialSequence(state, action)
		{
			state.initialSequenceId = action.payload; // payload = sequence id

			state.update = state.update + 1;
		},
		setCurrentStep(state, action)
		{
			state.currentStepId = action.payload; // payload = step id
		},
		setProjectSize(state, action)
		{
			state.projectSize = action.payload; // payload = step id
		},
		addStepToHistory(state, action: {payload: string})
		{
			state.stepHistorySimulation.push(action.payload); // payload = step id
		},
		clearStepToHistory(state, action: PayloadAction<undefined>)
		{
			state.stepHistorySimulation = [];
		},
		goBackInStepHistory(state, action)
		{
			if (state.stepHistorySimulation.length < action.payload || action.payload < 1)
			{
				return;
			}

			const stepId = state.stepHistorySimulation[state.stepHistorySimulation.length - action.payload];
			const stepData = state.elementData[stepId];
			if (stepData.simulationSkip)
			{
				if (state.stepHistorySimulation.length >= action.payload + 1)
				{
					action.payload++;
				}
				else
				{
					return;
				}
			}

			for (let i = 0; i < action.payload; i++)
			{
				state.stepHistorySimulation.pop();
			}
		},
		clearScenario(state, action: PayloadAction<undefined>)
		{
			return initialState;
		},
		copyScenarioElement(state, action)
		{
			state.clipboard = action.payload; // payload = element id
		},
		updateScenario(state, action: PayloadAction<undefined>)
		{
			state.update = state.update + 1;
		},
		addSequenceConnection(state, action)
		{
			const { source, target } = action.payload;

			state.elementData[source].outputId = target;

			state.update = state.update + 1;
		},
		removeSequenceConnection(state, action)
		{
			const { source } = action.payload;

			state.elementData[source].outputId = "";

			state.update = state.update + 1;
		},
		setRenameTarget(state, action)
		{
			state.renameTarget = action.payload; // payload = element id
		},
		modifyStepSelection(state, action: { payload: string[] })
		{
			if (!action.payload)
			{
				console.log("clearing selection");
				state.selectedSteps = [];
				return;
			}

			for (const stepId of action.payload)
			{
				if (state.selectedSteps.includes(stepId))
				{
					state.selectedSteps = state.selectedSteps.filter((id) => { return id !== stepId });
				}
				else
				{
					state.selectedSteps.push(stepId);
				}
			}
		},
		setSequenceEditorOption(state, action)
		{
			if (!state.sequenceEditorOptions)
			{
				state.sequenceEditorOptions = defaultSequenceEditorOptions;
			}
			state.sequenceEditorOptions[action.payload.option] = action.payload.value;
		},
		setSequencesTopbarDraw(state, action)
		{
			state.sequencesTopbarDraw = action.payload; // payload = array of sequence ids
		},
		moveSequencesTopbarItem(state, action)
		{
			const newArray = state.sequencesTopbarDraw.filter((item, idx) => idx !== action.payload.item);
			newArray.splice(action.payload.index, 0, state.sequencesTopbarDraw[action.payload.item]);

			state.sequencesTopbarDraw = newArray;
		},
		setScenarioPublishInfo(state, action: { payload: IPublishInfo | null })
		{
			state.scenarioPublishInfo = action.payload; // payload is publish_info {} received from CMS, or manually set when publish is done
		},
		addReport(state, action)
		{
			if(!state.healthReports[action.payload.step])
			{
				state.healthReports[action.payload.step] = [];
			}
			state.healthReports[action.payload.step].push(action.payload);
		},
		removeReportAt(state, action)
		{
			if(!state.healthReports[action.payload.report.step]) return;

			state.healthReports[action.payload.report.step].splice(action.payload.index, 1);

			if(state.healthReports[action.payload.report.step].length === 0)
			{
				delete state.healthReports[action.payload.report.step];
			}
		},
		clearReports(state, action: PayloadAction<undefined>)
		{
			state.healthReports = {};
		},
		setProjectState(state, action: PayloadAction<ProjectStates>)
		{
			state.projectState = action.payload;
		},
		setPermissions(state, action: PayloadAction<Partial<IProjectPermissions>>)
		{
			state.permissions = { ...state.permissions, ...action.payload };
		},
		setProjectSaveFileId(state, action: PayloadAction<string>)
		{
			state.projectSaveFileId = action.payload;
		},
		setCurrentRevision(state, action: PayloadAction<ProjectRevision>)
		{
			state.currentRevision = action.payload;
		},
		setRevisions(state, action: PayloadAction<ProjectRevision[]>)
		{
			state.revisions = action.payload;
			state.highestRevisionNumber = action.payload.reduce((max, revision) => Math.max(max, revision.revisionNumber), 0);

		},
	},
});

export default ScenarioSlice;
